/**
 * Captures the browser's `beforeinstallprompt` so the OFFLINE OPT-IN flow can
 * offer a real "Install StableTrack" button instead of leaving the user to find
 * the address-bar icon. Installing matters for offline: an installed PWA gets a
 * standalone window, reliable persistent storage (clears the durability
 * advisory), and the strongest eviction protection for the on-device replica.
 *
 * The module registers its listeners at import time (it is exported from the
 * offline barrel, which App.tsx imports eagerly) because Chromium fires
 * `beforeinstallprompt` once, early in the page lifecycle — a lazily-mounted
 * Settings page would miss it. With the offline build flag OFF, registration is
 * skipped entirely (see the gate below): capturing the event suppresses the
 * browser's own install promo, which would be a flag-off behavior change.
 */
import { OFFLINE_AVAILABLE } from './entities';

let deferredPrompt: any = null;
const listeners = new Set<() => void>();

/** Remembers a completed install on this device, so a later BROWSER-TAB visit
 *  can say "open the app you already have" instead of offering an install. */
const APP_INSTALLED_KEY = 'st.offline.appInstalled';

function rememberInstalled(): void {
  try {
    localStorage.setItem(APP_INSTALLED_KEY, 'true');
  } catch {
    /* best-effort */
  }
}

function notify(): void {
  listeners.forEach((l) => l());
}

// Flag-gated: with the offline build flag off, register NOTHING — capturing the
// event suppresses the browser's own install promo, which would be a (small)
// behavior change in flag-off builds. Flag-off must stay byte-identical.
if (OFFLINE_AVAILABLE && typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e: any) => {
    e.preventDefault();
    deferredPrompt = e;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    rememberInstalled();
    notify();
  });
}

/** True when the captured native install prompt can be shown right now. */
export function canPromptInstall(): boolean {
  return deferredPrompt != null;
}

/**
 * True when the browser supports the one-click PWA install prompt at all
 * (`beforeinstallprompt` is Chromium-only — Chrome/Edge). False on Safari and
 * Firefox, where there's no programmatic install and no in-app prompt fires.
 * Feature-detected, not user-agent-sniffed.
 */
export function supportsInstallPrompt(): boolean {
  return typeof window !== 'undefined' && 'onbeforeinstallprompt' in window;
}

/** Best guess at an iOS/iPadOS device (where install = Safari → Add to Home Screen). */
export function isAppleMobile(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // iPadOS 13+ reports as Mac; the touch-point check disambiguates.
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && (navigator as any).maxTouchPoints > 1);
}

/** True when already running as an installed app (standalone window). */
export function isStandalone(): boolean {
  return (
    (typeof window !== 'undefined' &&
      window.matchMedia?.('(display-mode: standalone)')?.matches) ||
    (navigator as any).standalone === true // iOS Safari
  );
}

/** Show the captured native install prompt. A used prompt cannot be re-shown. */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferredPrompt) return 'unavailable';
  const prompt = deferredPrompt;
  deferredPrompt = null; // single-use, per the spec
  notify();
  prompt.prompt();
  const { outcome } = await prompt.userChoice;
  if (outcome === 'accepted') rememberInstalled();
  return outcome;
}

/** True when an install completed on this device (even if currently in a tab). */
export function wasInstalledOnDevice(): boolean {
  if (isStandalone()) return true;
  try {
    return localStorage.getItem(APP_INSTALLED_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Re-render hook for components showing the Install button. */
export function subscribeInstallAvailability(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Test-only: clear captured state between tests. */
export function _resetForTests(): void {
  deferredPrompt = null;
  listeners.clear();
}
